import PublicLayout from "@/components/layout/PublicLayout";
import { useState } from "react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            await submitContact(form);
            toast.success("Thanks — we’ll get back to you within one business day.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error("Could not send message. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-16 pb-10" data-testid="contact-page">
                    <div className="bl-eyebrow">Support</div>
                    <h1 className="mt-5 font-display font-semibold text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[1.02] text-bl max-w-3xl">
                        Real{" "}
                        <span className="font-italic-accent text-bl-primary">
                            help
                        </span>{" "}
                        from real humans.
                    </h1>
                    <p className="mt-6 text-bl-muted text-lg max-w-2xl leading-relaxed">
                        Failed uploads, payment questions, refunds, account issues —
                        send us a note and we'll get back to you within one business
                        day.
                    </p>
                </div>
            </section>

            <section className="bl-container py-12 grid md:grid-cols-2 gap-10">
                <form
                    onSubmit={onSubmit}
                    className="bl-card p-7 md:p-9 space-y-4"
                    data-testid="contact-form"
                >
                    <div>
                        <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                            Your name
                        </label>
                        <input
                            required
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                            data-testid="contact-name-input"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                            Email
                        </label>
                        <input
                            required
                            type="email"
                            value={form.email}
                            onChange={(e) =>
                                setForm({ ...form, email: e.target.value })
                            }
                            className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                            data-testid="contact-email-input"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                            Subject (optional)
                        </label>
                        <input
                            value={form.subject}
                            onChange={(e) =>
                                setForm({ ...form, subject: e.target.value })
                            }
                            className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                            data-testid="contact-subject-input"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                            Message
                        </label>
                        <textarea
                            required
                            rows={6}
                            value={form.message}
                            onChange={(e) =>
                                setForm({ ...form, message: e.target.value })
                            }
                            className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                            data-testid="contact-message-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="bl-btn-primary"
                        data-testid="contact-submit-btn"
                    >
                        {submitting ? "Sending…" : "Send message"}
                    </button>
                </form>

                <div className="space-y-6">
                    <div className="bl-card p-7">
                        <h3 className="font-display font-semibold text-xl text-bl">
                            Common topics
                        </h3>
                        <ul className="mt-4 space-y-2.5 text-bl">
                            {[
                                "Failed uploads or unsupported files",
                                "Payment, refund, or billing questions",
                                "Account access or login issues",
                                "Subscription changes",
                                "Report regeneration",
                            ].map((t) => (
                                <li key={t} className="flex gap-2.5 leading-relaxed">
                                    <span className="text-bl-primary">·</span>
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="bl-card p-7">
                        <h3 className="font-display font-semibold text-xl text-bl">
                            What to expect
                        </h3>
                        <p className="mt-3 text-bl-muted leading-relaxed">
                            We reply within one business day for most requests. If
                            you've had a payment fail or a report didn't generate,
                            please include any order or report ID — it helps us resolve
                            things faster.
                        </p>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
